import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import { Home as HomeIcon, ExternalLink } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Emerald pin (SVG) so we don't depend on external CDN assets that 404 under React build
const emeraldPinIcon = new L.DivIcon({
    className: "borgo-map-pin",
    html: `
        <div class="relative">
            <svg width="34" height="46" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 0C7.6 0 0 7.6 0 17c0 12.3 15.4 27.4 16 28 0.5 0.5 1.5 0.5 2 0 0.6-0.6 16-15.7 16-28C34 7.6 26.4 0 17 0z" fill="#059669" stroke="white" stroke-width="2"/>
                <circle cx="17" cy="17" r="6" fill="white"/>
            </svg>
        </div>`,
    iconSize: [34, 46],
    iconAnchor: [17, 46],
    popupAnchor: [0, -42],
});

/**
 * PublicImmobiliMap
 * - items: array of immobili with ubicazione.latitudine / longitudine
 * - fallbackCenter: [lat, lng] to use if no items have coords (defaults to Tadasuni ~40.083, 8.916)
 * - height: string, e.g. "500px"
 * - variant: "full" | "teaser"
 */
export default function PublicImmobiliMap({
    items = [],
    fallbackCenter = [40.0833, 8.9167],
    height = "500px",
    variant = "full",
    borgoSlug = "tadasuni",
}) {
    const geoItems = useMemo(
        () =>
            (items || []).filter(
                (it) =>
                    typeof it?.ubicazione?.latitudine === "number" &&
                    typeof it?.ubicazione?.longitudine === "number"
            ),
        [items]
    );

    const center = useMemo(() => {
        if (geoItems.length === 0) return fallbackCenter;
        // Average all coords for initial focus
        const sum = geoItems.reduce(
            (acc, it) => {
                acc[0] += it.ubicazione.latitudine;
                acc[1] += it.ubicazione.longitudine;
                return acc;
            },
            [0, 0]
        );
        return [sum[0] / geoItems.length, sum[1] / geoItems.length];
    }, [geoItems, fallbackCenter]);

    // Empty state
    if (geoItems.length === 0) {
        return (
            <div
                className={`rounded-3xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 flex items-center justify-center text-center px-6 py-10 ${
                    variant === "teaser" ? "" : ""
                }`}
                style={{ minHeight: variant === "teaser" ? "260px" : "340px" }}
                data-testid="immobili-map-empty"
            >
                <div>
                    <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 mx-auto mb-3 flex items-center justify-center">
                        <HomeIcon size={22} />
                    </div>
                    <div className="font-serif text-xl text-stone-800 mb-1">
                        Nessun immobile georeferenziato al momento
                    </div>
                    <div className="text-sm text-stone-500 max-w-md mx-auto">
                        Non appena verranno associate le coordinate agli immobili pubblicati, appariranno qui come pin cliccabili.
                    </div>
                </div>
            </div>
        );
    }

    const zoom = geoItems.length === 1 ? 16 : variant === "teaser" ? 13 : 14;

    return (
        <div
            className="rounded-3xl overflow-hidden border border-stone-200 shadow-sm relative"
            style={{ height }}
            data-testid={variant === "teaser" ? "immobili-map-teaser" : "immobili-map-full"}
        >
            <MapContainer
                center={center}
                zoom={zoom}
                scrollWheelZoom={variant !== "teaser"}
                style={{ height: "100%", width: "100%" }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {geoItems.map((it) => {
                    const cover = it.marketing?.foto_copertina || it.marketing?.galleria?.[0] || "";
                    const coverUrl = cover
                        ? cover.startsWith("http")
                            ? cover
                            : `${process.env.REACT_APP_BACKEND_URL}${cover}`
                        : "";
                    const desc =
                        it.marketing?.descrizione_pubblica ||
                        it.marketing?.descrizione_narrativa ||
                        it.marketing?.titolo_annuncio ||
                        "";
                    return (
                        <Marker
                            key={it.id}
                            position={[it.ubicazione.latitudine, it.ubicazione.longitudine]}
                            icon={emeraldPinIcon}
                        >
                            <Popup maxWidth={280} minWidth={240} closeButton>
                                <div
                                    className="borgo-popup"
                                    data-testid={`immobili-map-popup-${it.codice || it.id}`}
                                    style={{ minWidth: 240 }}
                                >
                                    {coverUrl ? (
                                        <div
                                            style={{
                                                width: "100%",
                                                height: 140,
                                                borderRadius: 10,
                                                overflow: "hidden",
                                                marginBottom: 10,
                                                background: "#f5f5f4",
                                            }}
                                        >
                                            <img
                                                src={coverUrl}
                                                alt={it.anagrafica?.denominazione || "immobile"}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                width: "100%",
                                                height: 140,
                                                borderRadius: 10,
                                                marginBottom: 10,
                                                background: "#ecfdf5",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <HomeIcon size={32} color="#059669" />
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: 1,
                                            color: "#b45309",
                                            textTransform: "uppercase",
                                            marginBottom: 2,
                                        }}
                                    >
                                        {it.codice}{" "}
                                        {it.classificazione?.tipologia
                                            ? "· " + it.classificazione.tipologia
                                            : ""}
                                    </div>
                                    <div
                                        style={{
                                            fontFamily: "Georgia, serif",
                                            fontSize: 17,
                                            fontWeight: 600,
                                            color: "#1c1917",
                                            lineHeight: 1.2,
                                            marginBottom: 6,
                                        }}
                                    >
                                        {it.anagrafica?.denominazione || "Immobile"}
                                    </div>
                                    {desc && (
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "#57534e",
                                                lineHeight: 1.5,
                                                marginBottom: 10,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {desc}
                                        </div>
                                    )}
                                    <Link
                                        to={`/immobili/${it.id}?borgo=${borgoSlug}`}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "7px 14px",
                                            borderRadius: 999,
                                            background: "#059669",
                                            color: "white",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            textDecoration: "none",
                                        }}
                                        data-testid={`immobili-map-link-${it.codice || it.id}`}
                                    >
                                        Vedi scheda <ExternalLink size={12} />
                                    </Link>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
}
